/**
 * NotificationPreferences Component
 * Allows users to manage their notification settings for various categories and channels.
 */

'use client';

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Icons } from '../../constants';
import {
    getNotificationPreferences,
    updateNotificationPreference,
    NotificationPreference
} from '../../services/notificationService';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

const NOTIFICATION_CATEGORIES = [
    { id: 'board_invitations', label: 'Board Invitations', icon: 'Mail' },
    { id: 'comments', label: 'Comments', icon: 'MessageSquare' },
    { id: 'mentions', label: 'Mentions', icon: 'User' },
    { id: 'system_updates', label: 'System Updates', icon: 'Bell' },
    { id: 'collaboration', label: 'Collaboration', icon: 'Users' }
];

const CHANNELS = [
    { id: 'in_app', label: 'In-App', icon: 'Monitor' },
    { id: 'email', label: 'Email', icon: 'Mail' },
    { id: 'push', label: 'Push Notifications', icon: 'Bell' }
];

export const NotificationPreferences: React.FC = () => {
    const [preferences, setPreferences] = useState<NotificationPreference[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [saving, setSaving] = useState<string | null>(null);

    useEffect(() => {
        loadPreferences();
    }, []);

    const loadPreferences = async () => {
        setIsLoading(true);
        const result = await getNotificationPreferences();
        if (result.success) {
            setPreferences(result.data);
        }
        setIsLoading(false);
    };

    const handleToggle = async (category: string, channel: 'in_app' | 'email' | 'push') => {
        const key = `${category}-${channel}`;
        setSaving(key);

        const currentPref = preferences.find(p => p.category === category && p.channel === channel);
        const newState = currentPref ? !currentPref.enabled : false;

        const result = await updateNotificationPreference(category, channel, newState);
        if (result.success) {
            setPreferences(prev => {
                const existing = prev.findIndex(p => p.category === category && p.channel === channel);
                if (existing >= 0) {
                    const next = [...prev];
                    next[existing] = { ...next[existing], enabled: newState };
                    return next;
                }
                return [...prev, { category, channel, enabled: newState }];
            });
        }
        setSaving(null);
    };

    const isEnabled = (category: string, channel: string) => {
        const pref = preferences.find(p => p.category === category && p.channel === channel);
        return pref ? pref.enabled : true; // Default to true if not set
    };

    if (isLoading) {
        return (
            <div className="p-8 flex justify-center">
                <div className="animate-spin w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full" />
            </div>
        );
    }

    return (
        <div className="p-4 bg-white dark:bg-[#1e1e1e] rounded-xl border border-gray-100 dark:border-[#2a2a2a] shadow-xl max-w-lg mx-auto">
            {/* Compact Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <Icons.Settings className="w-4 h-4 text-indigo-500" />
                        Notification Preferences
                    </h3>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">Manage how and when you receive updates.</p>
                </div>
                <div className="flex items-center gap-1.5 text-[10px] text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-500/10 py-1 px-2 rounded-md font-medium">
                    <Icons.CheckCircle className="w-3 h-3" />
                    <span>Saved automatically</span>
                </div>
            </div>

            <div className="relative">
                {/* Header Row */}
                <div className="grid grid-cols-[1fr_50px_50px_50px] sm:grid-cols-[1fr_60px_60px_60px] gap-2 pb-2 border-b border-gray-100 dark:border-[#333] text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500 text-center">
                    <div className="text-left pl-1">Activity</div>
                    {CHANNELS.map(channel => (
                        <div key={channel.id} className="flex flex-col items-center justify-end gap-1 group relative">
                            {React.createElement((Icons as any)[channel.icon], { className: "w-3.5 h-3.5 mb-0.5" })}
                            <span className="hidden sm:block whitespace-nowrap">{channel.label.split(' ')[0]}</span>
                        </div>
                    ))}
                </div>

                {/* Rows */}
                <div className="divide-y divide-gray-50 dark:divide-[#252525]">
                    {NOTIFICATION_CATEGORIES.map((cat) => (
                        <div key={cat.id} className="py-3 grid grid-cols-[1fr_50px_50px_50px] sm:grid-cols-[1fr_60px_60px_60px] gap-2 items-center hover:bg-gray-50/50 dark:hover:bg-white/[0.02] rounded-lg transition-colors -mx-2 px-2">
                            {/* Category Label */}
                            <div className="flex items-center gap-2.5 overflow-hidden">
                                <div className="p-1.5 bg-indigo-50 dark:bg-indigo-500/10 rounded-md shrink-0 text-indigo-600 dark:text-indigo-400">
                                    {React.createElement((Icons as any)[cat.icon] || Icons.Bell, { className: "w-3.5 h-3.5" })}
                                </div>
                                <span className="font-semibold text-xs text-gray-700 dark:text-gray-200 truncate">{cat.label}</span>
                            </div>

                            {/* Toggles */}
                            {CHANNELS.map((channel) => {
                                const enabled = isEnabled(cat.id, channel.id);
                                const isSaving = saving === `${cat.id}-${channel.id}`;

                                return (
                                    <div key={channel.id} className="flex items-center justify-center">
                                        <button
                                            onClick={() => handleToggle(cat.id, channel.id as any)}
                                            disabled={isSaving}
                                            className={cn(
                                                "relative inline-flex h-4 w-7 shrink-0 cursor-pointer rounded-full border border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:ring-offset-2",
                                                enabled ? "bg-indigo-600" : "bg-gray-200 dark:bg-gray-700",
                                                isSaving && "opacity-50 cursor-wait"
                                            )}
                                        >
                                            <span className="sr-only">Use setting</span>
                                            <span
                                                aria-hidden="true"
                                                className={cn(
                                                    "pointer-events-none inline-block h-2.5 w-2.5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out mt-[2px] ml-[2px]",
                                                    enabled ? "translate-x-3" : "translate-x-0"
                                                )}
                                            />
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};
